import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import PermissionGate from '../../../components/PermissionGate';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { docTypeLabel, humanizeFieldKey } from '../../../utils/docTypeLabels';
import { formatDate } from '../../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const REVIEW_THRESHOLD = 70;

// Derive the visible status from the persisted AI fields. `processing` means
// the row exists but the pipeline either hasn't run yet or hasn't reached the
// classification write — the file is supported but we have no result yet.
function deriveStatus(doc) {
  if (doc?.pipelineError) return 'failed';
  const conf = doc?.aiClassification?.confidence;
  if (conf == null) return 'processing';
  if (conf < REVIEW_THRESHOLD) return 'review';
  return 'complete';
}

const STATUS_STYLES = {
  complete:   { bg: '#dcfce7', fg: '#15803d', labelKey: 'screens.claimsScreen.detail.docStatusComplete' },
  review:     { bg: '#fef3c7', fg: '#b45309', labelKey: 'screens.claimsScreen.detail.docStatusReview' },
  processing: { bg: '#e0e7ff', fg: '#3730a3', labelKey: 'screens.claimsScreen.detail.docStatusProcessing' },
  failed:     { bg: '#fee2e2', fg: '#b91c1c', labelKey: 'screens.claimsScreen.detail.docStatusFailed' },
};

const DocumentsTab = ({ claim, refreshClaim, S }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || 'en').split('-')[0];
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailsById, setDetailsById] = useState({});
  const [showRawId, setShowRawId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, name } | null
  const fileInputRef = useRef(null);

  const handleFiles = async files => {
    if (!files || files.length === 0) return;
    const claimId = claim?._id;
    if (!claimId) {
      toast.error(t('screens.claimsScreen.detail.claimIdUnavailable'));
      return;
    }
    setUploading(true);
    let uploaded = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await axios.post(`${BASE_URL}/claims/${claimId}/upload`, fd, { withCredentials: true });
        uploaded++;
      } catch (err) {
        toast.error(t('screens.claimsScreen.detail.uploadFailed', { name: file.name, msg: err.response?.data?.message || err.message }));
      }
    }
    if (uploaded > 0) {
      toast.success(t('screens.claimsScreen.detail.filesUploaded', { count: uploaded }));
      refreshClaim?.();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Delete a document via the existing fileExchange DELETE endpoint.
  // The row-level trash button sets pendingDelete; the ConfirmDialog at
  // the bottom of the tab runs the actual request. Audit entry is
  // written server-side by fileExchangeController.deleteFile.
  const runDelete = async (docId) => {
    await axios.delete(`${BASE_URL}/fileExchange/${docId}`, { withCredentials: true });
    toast.success(t('screens.claimsScreen.detail.docDeleted', { defaultValue: 'Document deleted.' }));
    refreshClaim?.();
  };

  // Fetch the full FileExchange doc (extractedFields, piiCaptions, raw text)
  // on demand. Cache per-id so toggling open→closed→open doesn't refetch.
  const toggleExpand = async docId => {
    if (!docId) return;
    if (expandedId === docId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(docId);
    if (!detailsById[docId]) {
      try {
        const resp = await axios.get(`${BASE_URL}/documents/${docId}`, { withCredentials: true });
        setDetailsById(prev => ({ ...prev, [docId]: resp.data?.data || null }));
      } catch (err) {
        toast.error(t('screens.claimsScreen.detail.loadDocumentFailed', { msg: err.response?.data?.message || err.message }));
      }
    }
  };

  return (
    <div>
      <PermissionGate resource='documents' action='upload'>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept='.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png'
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          style={{
            border: `2px dashed ${dragOver ? S.burgundy : S.borderLight}`,
            borderRadius: 14,
            padding: '32px 20px',
            textAlign: 'center',
            marginBottom: 24,
            background: dragOver ? 'rgba(128,0,32,0.03)' : 'transparent',
            transition: 'all 0.2s',
            cursor: uploading ? 'wait' : 'pointer',
          }}
        >
        {uploading ? (
          <div style={{ fontSize: 14, fontWeight: 600, color: S.burgundy }}>{t('screens.claimsScreen.detail.uploading')}</div>
        ) : (
          <>
            <svg
              width='28'
              height='28'
              viewBox='0 0 24 24'
              fill='none'
              stroke={dragOver ? S.burgundy : '#94a3b8'}
              strokeWidth='1.8'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
              <polyline points='17 8 12 3 7 8' />
              <line x1='12' y1='3' x2='12' y2='15' />
            </svg>
            <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, marginTop: 10 }}>{t('screens.claimsScreen.detail.dropDocuments')}</div>
            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{t('screens.claimsScreen.detail.docsUploadHint')}</div>
          </>
        )}
        </div>
      </PermissionGate>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(claim?.documents || []).map((doc, i) => {
          const status = deriveStatus(doc);
          const statusStyle = STATUS_STYLES[status];
          const typeKey = doc.aiClassification?.documentType;
          const confidence = doc.aiClassification?.confidence;
          const isExpanded = expandedId === (doc._id || doc.id);
          const details = detailsById[doc._id || doc.id];
          return (
            <div
              key={doc._id || doc.id || i}
              style={{
                background: S.bgCard,
                border: `1px solid ${S.borderLight}`,
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <div
                onClick={() => toggleExpand(doc._id || doc.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '14px 18px',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.015)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    background: `linear-gradient(135deg, ${S.burgundy}12, ${S.burgundy}08)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke={S.burgundy} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                    <polyline points='14 2 14 8 20 8' />
                    <line x1='16' y1='13' x2='8' y2='13' />
                    <line x1='16' y1='17' x2='8' y2='17' />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.originalName || doc.name || t('screens.claimsScreen.detail.documentDefault')}
                  </div>
                  <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
                    {t('screens.claimsScreen.detail.uploadedBy', {
                      date: doc.uploadedAt ? formatDate(doc.uploadedAt) : doc.createdAt ? formatDate(doc.createdAt) : doc.uploaded || '—',
                      by: doc.uploadedBy?.name || doc.uploadedByName || doc.by || '—',
                    })}
                  </div>
                </div>
                {typeKey && confidence != null && (
                  <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${S.burgundy}10`, color: S.burgundy, whiteSpace: 'nowrap' }}>
                    {docTypeLabel(typeKey, lang)} — {confidence}%
                  </span>
                )}
                <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.fg, whiteSpace: 'nowrap' }}>
                  {t(statusStyle.labelKey)}
                </span>
                {/* Open (inline PDF view). Uses /documents/:id/view which
                    decrypts .encrypt-suffixed live-uploaded files server-side
                    and streams inline plaintext. Prior to 2026-07-14 there was
                    only a download icon and encrypted files opened as
                    unreadable ciphertext. */}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (doc._id) {
                      window.open(`/api/v1/documents/${doc._id}/view`, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}
                  title={t('screens.claimsScreen.detail.openTitle', { defaultValue: 'Open' })}
                >
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke={S.textMuted} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
                    <polyline points='15 3 21 3 21 9' />
                    <line x1='10' y1='14' x2='21' y2='3' />
                  </svg>
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (doc._id && claim._id) {
                      window.location.href = `/api/v1/claims/${claim._id}/documents/${doc._id}/download`;
                    }
                  }}
                  style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}
                  title={t('screens.claimsScreen.detail.downloadTitle')}
                >
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke={S.textMuted} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                    <polyline points='7 10 12 15 17 10' />
                    <line x1='12' y1='15' x2='12' y2='3' />
                  </svg>
                </button>
                <PermissionGate resource='documents' action='delete'>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setPendingDelete({ id: doc._id, name: doc.originalName || doc.name || 'document' });
                    }}
                    style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}
                    title={t('common.delete', { defaultValue: 'Delete' })}
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#dc2626' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                      <polyline points='3 6 5 6 21 6' />
                      <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' />
                    </svg>
                  </button>
                </PermissionGate>
              </div>
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${S.borderLight}`, padding: '16px 18px', background: '#fafafa' }}>
                  {!details ? (
                    <div style={{ fontSize: 13, color: S.textMuted }}>{t('screens.claimsScreen.detail.loading')}</div>
                  ) : (
                    <>
                      {details.pipelineError && (
                        <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c', marginBottom: 4 }}>{t('screens.claimsScreen.detail.processingFailed')}</div>
                          <div style={{ fontSize: 12, color: '#7f1d1d', wordBreak: 'break-word' }}>{details.pipelineError}</div>
                        </div>
                      )}
                      {status === 'review' && (
                        <div style={{ padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#92400e' }}>
                          {t('screens.claimsScreen.detail.lowConfidenceWarn')}
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 14 }}>
                        {Object.entries(details.extractedFields || {})
                          .filter(([, v]) => v != null && v !== '')
                          .map(([k, v]) => (
                            <div key={k} style={{ background: '#fff', padding: '10px 12px', borderRadius: 8, border: `1px solid ${S.borderLight}` }}>
                              <div style={{ fontSize: 11, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>
                                {humanizeFieldKey(k, lang)}
                              </div>
                              <div style={{ fontSize: 13, color: S.textPrimary, wordBreak: 'break-word' }}>
                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </div>
                            </div>
                          ))}
                        {Object.keys(details.extractedFields || {}).length === 0 && (
                          <div style={{ fontSize: 12, color: S.textMuted }}>{t('screens.claimsScreen.detail.noFieldsExtracted')}</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: S.textMuted, marginBottom: 10 }}>
                        <span>
                          <span style={{ color: '#10b981', fontWeight: 600 }}>● </span>
                          {t('screens.claimsScreen.detail.piiEntities', { count: (details.piiCaptions || []).length })}
                        </span>
                        {details.matchedClaim && <span>{t('screens.claimsScreen.detail.autoFiled')}</span>}
                      </div>
                      {details.textContent && (
                        <div>
                          <button
                            onClick={() => setShowRawId(showRawId === doc._id ? null : doc._id)}
                            style={{
                              padding: '6px 12px',
                              fontSize: 12,
                              fontWeight: 600,
                              color: S.burgundy,
                              background: 'transparent',
                              border: `1px solid ${S.burgundy}40`,
                              borderRadius: 6,
                              cursor: 'pointer',
                            }}
                          >
                            {showRawId === doc._id ? t('screens.claimsScreen.detail.hideRawText') : t('screens.claimsScreen.detail.showRawText')}
                          </button>
                          {showRawId === doc._id && (
                            <pre style={{
                              marginTop: 10,
                              padding: 12,
                              background: '#fff',
                              border: `1px solid ${S.borderLight}`,
                              borderRadius: 8,
                              fontSize: 11,
                              fontFamily: 'monospace',
                              color: S.textPrimary,
                              maxHeight: 280,
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}>
                              {details.textContent}
                            </pre>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {(claim?.documents || []).length === 0 && (
          <div style={{ padding: '20px 0', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('screens.claimsScreen.detail.noDocsYet')}</div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title={t('screens.claimsScreen.detail.deleteDocTitle', { defaultValue: 'Delete document?' })}
          message={t('screens.claimsScreen.detail.deleteDocMessage', {
            defaultValue: `"${pendingDelete.name}" will be permanently removed from this claim. An audit entry will be recorded.`,
            name: pendingDelete.name,
          })}
          confirmText={t('common.delete', { defaultValue: 'Delete' })}
          variant='danger'
          onConfirm={async () => {
            try {
              await runDelete(pendingDelete.id);
            } catch (err) {
              toast.error(err.response?.data?.message || t('screens.claimsScreen.detail.deleteFailed', { defaultValue: 'Failed to delete document.' }));
            } finally {
              setPendingDelete(null);
            }
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default DocumentsTab;
